import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, MapPin, Home, Square, Calendar, Check, MessageCircle } from 'lucide-react';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !project) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  const handleWhatsAppInquiry = () => {
    const message = `Hello, I am interested in ${project.title}. Please provide more details.`;
    window.open(`https://wa.me/919884611368?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDownloadBrochure = () => {
    // Demo download functionality
    alert(`Downloading brochure for ${project.title}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Under Construction':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Sold Out':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-yellow-600 shadow-2xl transform transition-all duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold text-yellow-600 mb-2">{project.title}</h2>
              <div className="flex items-center text-gray-600 mb-3">
                <MapPin className="h-5 w-5 mr-2 text-yellow-600" />
                <span className="text-lg">{project.location}</span>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-yellow-600 hover:text-yellow-700 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
            >
              <X className="h-8 w-8" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Image Carousel */}
          <div className="relative mb-8">
            <div className="aspect-video bg-gray-200 rounded-xl overflow-hidden">
              <img
                src={project.images[currentImageIndex]}
                alt={`${project.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-contain bg-white"
              />
            </div>
            
            {/* Carousel Controls */}
            {project.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all duration-200"
                >
                  <ChevronLeft className="h-6 w-6 text-yellow-600" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all duration-200"
                >
                  <ChevronRight className="h-6 w-6 text-yellow-600" />
                </button>
                
                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {project.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        currentImageIndex === index ? 'bg-yellow-600' : 'bg-white bg-opacity-60'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Project Details */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-yellow-600 mb-4">Project Details</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Home className="h-5 w-5 text-yellow-600 mr-3" />
                  <div>
                    <span className="font-medium text-gray-700">Configuration:</span>
                    <span className="ml-2 text-gray-600">{project.bhk}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Square className="h-5 w-5 text-yellow-600 mr-3" />
                  <div>
                    <span className="font-medium text-gray-700">Area:</span>
                    <span className="ml-2 text-gray-600">{project.area}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-yellow-600 mr-3" />
                  <div>
                    <span className="font-medium text-gray-700">Price Range:</span>
                    <span className="ml-2 text-gray-600 font-semibold">{project.price}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-yellow-600 mb-4">Features & Amenities</h3>
              <div className="grid grid-cols-1 gap-2">
                {project.features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <Check className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-yellow-600 mb-4">About This Project</h3>
            <p className="text-gray-600 leading-relaxed">{project.description}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleDownloadBrochure}
              className="flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 border border-gray-300"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Brochure
            </button>
            <button
              onClick={handleWhatsAppInquiry}
              className="flex items-center justify-center px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition-colors duration-200 flex-1"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Enquire on WhatsApp
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;